/* Reya — long-form reading helpers: auto table of contents, reading progress, share/copy, subtle parallax. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const slugify = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';

  /* ---------- <rr-toc data-target="#id" data-levels="h2,h3" data-min="2"> ---------- */
  if (!customElements.get('rr-toc')) {
    customElements.define(
      'rr-toc',
      class extends HTMLElement {
        connectedCallback() {
          if (this.dataset.rrBuilt) return;
          this.dataset.rrBuilt = '1';
          const target = document.querySelector(this.dataset.target);
          const list = this.querySelector('[data-rr-toc-list]');
          const wrap = this.closest('[data-rr-toc-wrap]');
          if (!target || !list) return this.empty(wrap);
          const headings = Array.from(target.querySelectorAll(this.dataset.levels || 'h2,h3')).filter((h) =>
            h.textContent.trim()
          );
          if (headings.length < parseInt(this.dataset.min || '2', 10)) return this.empty(wrap);

          const used = new Set();
          const frag = document.createDocumentFragment();
          this.links = [];
          headings.forEach((h) => {
            if (!h.id) {
              let id = slugify(h.textContent);
              let n = 2;
              while (used.has(id) || document.getElementById(id)) id = `${slugify(h.textContent)}-${n++}`;
              h.id = id;
            }
            used.add(h.id);
            const li = document.createElement('li');
            if (h.tagName !== 'H2') li.className = 'rr-toc__sub';
            const a = document.createElement('a');
            a.href = `#${h.id}`;
            a.textContent = h.textContent.trim();
            a.addEventListener('click', (e) => {
              e.preventDefault();
              h.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
              history.replaceState(null, '', `#${h.id}`);
              h.setAttribute('tabindex', '-1');
              h.focus({ preventScroll: true });
            });
            li.appendChild(a);
            frag.appendChild(li);
            this.links.push({ a, h });
          });
          list.appendChild(frag);
          this.hidden = false;
          if (wrap) wrap.removeAttribute('data-rr-toc-empty');

          if ('IntersectionObserver' in window) {
            const visible = new Map();
            const io = new IntersectionObserver(
              (entries) => {
                entries.forEach((en) => visible.set(en.target, en.isIntersecting));
                let active = null;
                for (const { h } of this.links) {
                  if (visible.get(h)) {
                    active = h;
                    break;
                  }
                }
                if (!active) {
                  // choose last heading above viewport
                  for (const { h } of this.links) if (h.getBoundingClientRect().top < 120) active = h;
                }
                this.links.forEach(({ a, h }) => {
                  const on = h === active;
                  a.classList.toggle('is-active', on);
                  if (on) a.setAttribute('aria-current', 'location');
                  else a.removeAttribute('aria-current');
                });
              },
              { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
            );
            this.links.forEach(({ h }) => io.observe(h));
          }
        }
        empty(wrap) {
          this.hidden = true;
          if (wrap) wrap.setAttribute('data-rr-toc-empty', '');
        }
      }
    );
  }

  /* ---------- <rr-reading-progress data-target="#id"> ---------- */
  if (!customElements.get('rr-reading-progress')) {
    customElements.define(
      'rr-reading-progress',
      class extends HTMLElement {
        connectedCallback() {
          this.bar = this.querySelector('.rr-progress__bar');
          this.target = document.querySelector(this.dataset.target) || document.body;
          this.ticking = false;
          this.onScroll = () => {
            if (this.ticking) return;
            this.ticking = true;
            requestAnimationFrame(() => {
              this.update();
              this.ticking = false;
            });
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
          this.update();
        }
        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
        update() {
          if (!this.bar) return;
          const r = this.target.getBoundingClientRect();
          const total = r.height - window.innerHeight;
          const p = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 1;
          this.bar.style.transform = `scaleX(${p})`;
          this.setAttribute('aria-valuenow', Math.round(p * 100));
        }
      }
    );
  }

  /* ---------- <rr-share> : [data-rr-copy] copies data-url ---------- */
  if (!customElements.get('rr-share')) {
    customElements.define(
      'rr-share',
      class extends HTMLElement {
        connectedCallback() {
          this.addEventListener('click', async (e) => {
            const copy = e.target.closest('[data-rr-copy]');
            const native = e.target.closest('[data-rr-native-share]');
            if (native && navigator.share) {
              e.preventDefault();
              try {
                await navigator.share({ title: this.dataset.title, url: this.dataset.url });
              } catch (err) {}
              return;
            }
            if (!copy) return;
            e.preventDefault();
            const url = this.dataset.url || location.href;
            try {
              await navigator.clipboard.writeText(url);
            } catch (err) {
              const t = document.createElement('textarea');
              t.value = url;
              document.body.appendChild(t);
              t.select();
              try {
                document.execCommand('copy');
              } catch (e2) {}
              t.remove();
            }
            copy.classList.add('is-copied');
            setTimeout(() => copy.classList.remove('is-copied'), 2000);
            if (window.Reya && window.Reya.toast) window.Reya.toast(this.dataset.copied || 'Copied');
          });
          const native = this.querySelector('[data-rr-native-share]');
          if (native && navigator.share) native.hidden = false;
        }
      }
    );
  }

  /* ---------- <rr-copy data-target="#id"> copies an element's text ---------- */
  if (!customElements.get('rr-copy')) {
    customElements.define(
      'rr-copy',
      class extends HTMLElement {
        connectedCallback() {
          const btn = this.querySelector('button');
          btn &&
            btn.addEventListener('click', async () => {
              const el = document.querySelector(this.dataset.target);
              if (!el) return;
              try {
                await navigator.clipboard.writeText(el.textContent);
                window.Reya && window.Reya.toast && window.Reya.toast(this.dataset.copied || 'Copied');
              } catch (e) {
                const r = document.createRange();
                r.selectNodeContents(el);
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(r);
              }
            });
        }
      }
    );
  }

  /* ---------- <rr-parallax data-speed="0.12"> subtle image parallax ---------- */
  if (!customElements.get('rr-parallax')) {
    customElements.define(
      'rr-parallax',
      class extends HTMLElement {
        connectedCallback() {
          if (reduceMotion) return;
          this.inner = this.firstElementChild;
          if (!this.inner) return;
          this.speed = parseFloat(this.dataset.speed || '0.12');
          this.onScroll = () => requestAnimationFrame(() => this.update());
          if ('IntersectionObserver' in window) {
            this.io = new IntersectionObserver((en) => {
              const vis = en[0].isIntersecting;
              if (vis) window.addEventListener('scroll', this.onScroll, { passive: true });
              else window.removeEventListener('scroll', this.onScroll);
            });
            this.io.observe(this);
          }
          this.update();
        }
        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          this.io && this.io.disconnect();
        }
        update() {
          const r = this.getBoundingClientRect();
          const center = r.top + r.height / 2 - window.innerHeight / 2;
          this.inner.style.transform = `translate3d(0, ${(-center * this.speed).toFixed(1)}px, 0) scale(1.12)`;
        }
      }
    );
  }
})();
